/**
 * The tenant change channel (D35): "something with this id changed", and
 * nothing else.
 *
 *     resource.changed { kind, id, op, version, changedBy }
 *
 * **IDs only, never data.** A client that hears a change invalidates what it
 * holds of that resource and re-reads it over REST, where the caller's grants
 * are checked as for any read — so the channel needs no per-resource
 * permission filter, and a change to something the caller may not read tells
 * them only that an id they cannot open moved.
 *
 * **Emitted by the storage ports, not the routes** (`@acp/handlers/changes.js`):
 * every writer — REST, WS row patches (rundown and playlist heads; rows keep
 * their per-rundown channel), replication from the site link and the peer
 * link, migrations, cloud-production events — goes through the same stores,
 * so nothing can write without being heard. Bursts are coalesced per
 * request/invocation: one frame per resource, its last state.
 *
 * **Tenant-scoped.** A connection hears its own tenant's changes only — the
 * tenant comes from its verified token (D13), never from the frame.
 *
 * `version` is the resource's optimistic-concurrency version after the write
 * (a row's `version` for rundown/playlist heads) when it has one, else null.
 * `changedBy` is who made the write when a user did (`displayName`: their
 * email, else their user id); null for a machine writer — replication, a
 * migration, a cloud-production event.
 */
import { z } from 'zod';

export const RESOURCE_KINDS = ['rundown', 'playlist', 'studio', 'showStyle', 'production', 'cloudProduction', 'site', 'node', 'user', 'group'] as const;
export const resourceKindSchema = z.enum(RESOURCE_KINDS);
export type ResourceKind = z.infer<typeof resourceKindSchema>;

export const changeOpSchema = z.enum(['upsert', 'delete']);
export type ChangeOp = z.infer<typeof changeOpSchema>;

export const changedBySchema = z.object({ userId: z.string().min(1).max(128), displayName: z.string().min(1).max(320) }).strict();
export type ChangedBy = z.infer<typeof changedBySchema>;

export const resourceChangeSchema = z
  .object({
    kind: resourceKindSchema,
    id: z.string().min(1).max(128),
    op: changeOpSchema,
    version: z.number().int().nonnegative().nullable(),
    changedBy: changedBySchema.nullable(),
  })
  .strict();
export type ResourceChange = z.infer<typeof resourceChangeSchema>;
